import { useAuth } from '../../auth/AuthContext';
import { useLeaderboard } from '@stans-world/sdk/react';
import { registeredGameSlugs } from '../../games/gameRegistry';
import { formatUsd } from '../../utils/formatCurrency';
import AnimatedUsdScore from './AnimatedUsdScore';

const PRIMARY_GAME_ID = registeredGameSlugs[0] || 'paper-fly';
const VISIBLE_TOP = 10;
const DUMMY_LEADERBOARD_ROWS = [
  { userId: 'demo_01', username: 'AcePilot', score: 9820 },
  { userId: 'demo_02', username: 'CloudRunner', score: 9175 },
  { userId: 'demo_03', username: 'SkylineKid', score: 8890 },
  { userId: 'demo_04', username: 'JetNomad', score: 8510 },
];

function withDisplayRanks(rows) {
  return rows.map((row, idx) => ({ ...row, rank: idx + 1 }));
}

function leaderboardWithDummyRows(topRows) {
  const realRows = Array.isArray(topRows) ? topRows : [];
  const seen = new Set(realRows.map((r) => String(r.userId)));
  const dummyRows = DUMMY_LEADERBOARD_ROWS.filter((r) => !seen.has(String(r.userId)));
  const merged = [...dummyRows, ...realRows];
  return withDisplayRanks(merged);
}

function isYouRow(user, row) {
  return Boolean(user && row && String(row.userId) === String(user.id));
}

function LeaderboardBlock({ title, top, self, user, emptyHint, listId }) {
  const rows = top.slice(0, VISIBLE_TOP);
  const you =
    user &&
    (top.find((r) => String(r.userId) === String(user.id)) ??
      (self && String(self.userId) === String(user.id) ? self : null));
  const youInSlice = Boolean(user && rows.some((r) => String(r.userId) === String(user.id)));

  return (
    <section className="leaderboard-panel" aria-label={title}>
      <p className="leaderboard-panel__title">{title}</p>
      <div className="leaderboard-list" id={listId} role="list">
        {rows.length === 0 ? (
          <p className="leaderboard-panel__empty">{emptyHint}</p>
        ) : (
          rows.map((row) => (
            <p
              key={`${listId}-${row.userId}-${row.rank}`}
              className={`leaderboard-row${
                user && String(row.userId) === String(user.id) ? ' leaderboard-row--you' : ''
              }`}
              role="listitem"
            >
              <span className="leaderboard-row__rank">{row.rank}.</span>
              <span className="leaderboard-row__name">{row.username}</span>
              <span className="leaderboard-row__score">
                {isYouRow(user, row) ? <AnimatedUsdScore score={row.score} /> : formatUsd(row.score)}
              </span>
            </p>
          ))
        )}
        {you && !youInSlice ? (
          <>
            <div className="leaderboard-sep" aria-hidden="true" />
            <p className="leaderboard-row leaderboard-row--you" role="listitem">
              <span className="leaderboard-row__rank">{you.rank}.</span>
              <span className="leaderboard-row__name">You</span>
              <span className="leaderboard-row__score">
                <AnimatedUsdScore score={you.score} />
              </span>
            </p>
          </>
        ) : null}
      </div>
    </section>
  );
}

function SidebarLeaderboardSection() {
  const { user, authChecked } = useAuth();
  const { daily, allTime } = useLeaderboard(PRIMARY_GAME_ID);
  const dailyTop = leaderboardWithDummyRows(daily.top);
  const allTimeTop = leaderboardWithDummyRows(allTime.top);

  if (!authChecked) {
    return (
      <div className="sidebar-secondary">
        <div className="sidebar-divider" aria-hidden="true" />
        <h2 id="sidebar-leaderboards-heading" className="sidebar-section-heading">
          Leaderboards
        </h2>
        <section className="leaderboard-panel" aria-labelledby="sidebar-leaderboards-heading">
          <p className="leaderboard-panel__empty">Loading…</p>
        </section>
      </div>
    );
  }

  return (
    <div className="sidebar-secondary">
      <div className="sidebar-divider" aria-hidden="true" />
      <h2 id="sidebar-leaderboards-heading" className="sidebar-section-heading">
        Leaderboards
      </h2>
      <LeaderboardBlock
        title="Today"
        top={dailyTop}
        self={daily.self}
        user={user}
        emptyHint="No flights today yet."
        listId="leaderboard-daily"
      />
      <LeaderboardBlock
        title="All time"
        top={allTimeTop}
        self={allTime.self}
        user={user}
        emptyHint="Fly farther to climb the board."
        listId="leaderboard-alltime"
      />
    </div>
  );
}

export default SidebarLeaderboardSection;
